import { createClient } from '@supabase/supabase-js'

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

// Get environment variables, providing fallbacks for SSR
const supabaseUrl = process.env.GATSBY_SUPABASE_URL || '';
const supabaseAnonKey = process.env.GATSBY_SUPABASE_ANON_KEY || '';

// Create a Supabase client with custom session settings
let supabase;

try {
  // Only create the client if we have the required config
  if (supabaseUrl && supabaseAnonKey) {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        storageKey: 'sod-authToken',
        detectSessionInUrl: true,
        // Enhanced storage mechanism that uses both localStorage and sessionStorage
        storage: {
          getItem: key => {
            try {
              // Check if in browser environment first
              if (typeof window === 'undefined') {
                return null;
              }
              
              // Try localStorage first (original method)
              const item = localStorage.getItem(key);
              if (item) return item;
              
              // Fall back to sessionStorage as backup
              return sessionStorage.getItem(key);
            } catch (e) {
              console.error('Storage access error:', e);
              return null;
            }
          },
          setItem: (key, value) => {
            try {
              // Only attempt in browser environment
              if (typeof window !== 'undefined') {
                localStorage.setItem(key, value);
                // Also store in sessionStorage as backup
                sessionStorage.setItem(key, value);
              }
            } catch (e) {
              console.error('Storage set error:', e);
            }
          },
          removeItem: key => {
            try {
              // Only attempt in browser environment
              if (typeof window !== 'undefined') {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
              }
            } catch (e) {
              console.error('Storage remove error:', e);
            }
          }
        }
      }
    });
  } else {
    console.warn('Missing Supabase configuration, using mock client');
    // Create a mock client that won't crash
    supabase = {
      auth: {
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        getUser: () => Promise.resolve({ data: { user: null }, error: null }),
        signOut: () => Promise.resolve({ error: null }),
      },
      from: (table) => ({
        select: () => Promise.resolve({ data: [], error: null }),
        insert: () => Promise.resolve({ data: null, error: null }),
        update: () => Promise.resolve({ data: null, error: null }),
        delete: () => Promise.resolve({ data: null, error: null }),
        filter: () => ({ select: () => Promise.resolve({ data: [], error: null }) }),
      }),
    };
  }
} catch (err) {
  console.error('Error initializing Supabase client:', err);
  // Provide a stub client that won't break code
  supabase = {
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signOut: () => Promise.resolve({ error: null }),
    },
    from: (table) => ({
      select: () => Promise.resolve({ data: [], error: null }),
      insert: () => Promise.resolve({ data: null, error: null }),
      update: () => Promise.resolve({ data: null, error: null }),
      delete: () => Promise.resolve({ data: null, error: null }),
      filter: () => ({ select: () => Promise.resolve({ data: [], error: null }) }),
    }),
  };
}

export { supabase };

// Create a function to safely check if supabase is ready for use
export const isSupabaseReady = () => 
  !!process.env.GATSBY_SUPABASE_URL && 
  !!process.env.GATSBY_SUPABASE_ANON_KEY;